import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  EyeIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { contentService } from "../../services";
import { openWhatsApp } from "../../lib/utils";
import SEO from "../SEO";

const OphthalmologyPage = () => {
  const { diseaseName } = useParams();
  const [diseaseData, setDiseaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    city: "",
    treatment: ""
  });

  // Available ophthalmology diseases
  const availableDiseases = [
    'cataract-surgery',
    'contoura-vision',
    'diabetic-retinopathy',
    'femto-lasik-surgery',
    'glaucoma-surgery',
    'icl-surgery',
    'lasik-eye-surgery',
    'phaco-surgery',
    'prk-lasik-surgery',
    'retinal-detachment',
    'smile-lasik-surgery',
    'squint-surgery',
    'vitrectomy-surgery'
  ];

  useEffect(() => {
    const loadDiseaseData = async () => {
      try {
        setLoading(true);

        if (!availableDiseases.includes(diseaseName)) {
          setDiseaseData(null);
          setLoading(false);
          return;
        }

        // Fetch data from API
        const data = await contentService.getOphthalmologyTreatment(diseaseName);
        
        if (data && data.title) {
          setDiseaseData(data);
        } else {
          console.error('Invalid data structure for:', diseaseName);
          setDiseaseData(null);
        }
      } catch (error) {
        console.error('Error loading disease data:', error);
        // Fallback to local JSON if API fails
        try {
          const fallbackData = await import(`../lib/data/ophthalmology/${diseaseName}.json`);
          if (fallbackData.default && fallbackData.default.title) {
            setDiseaseData(fallbackData.default);
          } else {
            setDiseaseData(null);
          }
        } catch (fallbackError) {
          console.error('Fallback data also failed:', fallbackError);
          setDiseaseData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadDiseaseData();
  }, [diseaseName]);

  const handleQuickBooking = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      // Simple validation - the main modal will handle full booking
      return;
    }
    // This is just a quick form - redirect to modal for full booking
    setFormData({ name: "", mobile: "", city: "", treatment: "" });
  };



  const formatDiseaseName = (name) => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getSeoImage = () => {
    switch (diseaseName) {
      case 'cataract-surgery':
      case 'phaco-surgery':
        return '/image/Cataract_2.png';
      case 'diabetic-retinopathy':
        return '/image/Diabetic_2.jpg';
      case 'retinal-detachment':
        return '/image/Retinal_2.jpg';
      case 'contoura-vision':
      case 'lasik-eye-surgery':
        return '/image/Lasik_eye_surgery_2.jpg';
      case 'glaucoma-surgery':
        return '/image/Glaucoma_2.jpg';
      case 'icl-surgery':
        return '/image/ICL_SURGERY_2.jpg';
      case 'squint-surgery':
        return '/image/Squint_2.jpg';
      case 'vitrectomy-surgery':
        return '/image/Vitrectomy_2.jpg';
      case 'femto-lasik-surgery':
        return '/image/Lasik_eye_surgery_3.jpg';
      case 'smile-lasik-surgery':
        return '/image/Lasik_eye_surgery_4.jpg';
      case 'prk-lasik-surgery':
        return '/image/Lasik_eye_surgery_5.jpg';
      default:
        return '/image/seo_image.jpg';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading treatment information...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!diseaseData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <EyeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Treatment Not Found</h1>
          <p className="text-gray-600 mb-8">
            The ophthalmology treatment you're looking for is not available.
          </p>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Available Treatments:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-2xl mx-auto">
              {availableDiseases.map((disease) => (
                <Link
                  key={disease}
                  to={`/ophthalmology/${disease}`}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  {formatDiseaseName(disease)}
                </Link>
              ))}
            </div>
          </div>
          <Link to="/" className="mt-8 inline-block">
            <Button>Back to Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${formatDiseaseName(diseaseName)} Treatment`}
        description={
          diseaseData?.metaDescription ||
          (Array.isArray(diseaseData?.description)
            ? diseaseData.description.slice(0, 2).join(' ')
            : 'Get expert ophthalmology care with advanced, minimally invasive treatment options by top surgeons at TheLivCure.')
        }
        image={getSeoImage()}
        url={`/ophthalmology/${diseaseName}`}
        keywords={`${formatDiseaseName(diseaseName)}, ophthalmology, eye treatment, eye surgery, TheLivCure`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'MedicalProcedure',
          name: `${formatDiseaseName(diseaseName)} Treatment`,
          medicalSpecialty: 'Ophthalmologic',
          url: `https://thelivcure.com/ophthalmology/${diseaseName}`,
          provider: {
            '@type': 'MedicalOrganization',
            name: 'TheLivCure'
          }
        }}
      />
      <Header />

      {/* Hero Section */}
      <section className={`py-16 ${diseaseName === 'cataract-surgery' ? 'relative overflow-hidden' : 'bg-gradient-to-br from-blue-50 to-cyan-50'}`}>
        {/* Video Background for Cataract Surgery */}
        {diseaseName === 'cataract-surgery' && (
          <div className="absolute inset-0 z-0">
            <video 
              src="/videos/Cataract_vid.mp4" 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover"
            />
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
        )}
        
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${diseaseName === 'cataract-surgery' ? 'relative z-10' : ''}`}>
          <Breadcrumb
            items={[
              { label: "Ophthalmology", href: "/ophthalmology" },
              { label: diseaseData?.title || formatDiseaseName(diseaseName) }
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <Badge className="mb-4">Ophthalmology</Badge>
              <h1 className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 ${diseaseName === 'cataract-surgery' ? 'text-white' : 'text-gray-900'}`}>
                {diseaseData.title}
              </h1>
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {diseaseData.description.map((desc, index) => (
                  <p key={index} className={`text-sm sm:text-base lg:text-lg leading-relaxed ${diseaseName === 'cataract-surgery' ? 'text-gray-200' : 'text-gray-600'}`}>
                    {desc}
                  </p>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button size="lg" className="bg-gray-900 hover:bg-gray-600 text-sm sm:text-base" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from the TheLivCure site.")}>
                  <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="hidden sm:inline">Call: +91 93117 20045</span>
                  <span className="sm:hidden">Call Now</span>
                </Button>
                <BookAppointmentModal treatmentType={diseaseData?.title || formatDiseaseName(diseaseName)}>
                  <Button size="lg" variant="outline" className="text-sm sm:text-base">
                    <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="hidden sm:inline">Book Free Consultation</span>
                    <span className="sm:hidden">Book Consultation</span>
                  </Button>
                </BookAppointmentModal>
              </div>
            </div>

            {/* Quick Booking Form */}
            <Card className="shadow-xl mt-8 lg:mt-0">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                  Quick Consultation Request
                </h3>
                <form onSubmit={handleQuickBooking} className="space-y-3 sm:space-y-4">
                  <Input
                    placeholder="Patient Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="Mobile Number *"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    required
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="Treatment Needed"
                    value={formData.treatment}
                    onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                    className="text-sm sm:text-base"
                  />
                  <BookAppointmentModal
                    treatmentType={diseaseData?.title || formatDiseaseName(diseaseName)}
                    doctorName=""
                  >
                    <Button type="button" className="w-full bg-blue-600 hover:bg-blue-700 py-2.5 sm:py-3 text-sm sm:text-base">
                      Book Detailed Consultation
                    </Button>
                  </BookAppointmentModal>
                </form>

                {/* Quick Benefits */}
                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      <span>Free Consultation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      <span>Expert Doctors</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      <span>Same Day Appointment</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      <span>Advanced Technology</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    

      {/* Overview Section */}
      {diseaseData.overview && diseaseData.overview.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {formatDiseaseName(diseaseName)} Overview
              </h2>
              <p className="text-lg text-gray-600">
                Essential information about the condition and treatment
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {diseaseData.overview.map((section, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <EyeIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.heading}</h3>
                    <ul className="space-y-2">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-2">
                          <CheckCircleIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Diagnosis Section */}
      {diseaseData.diagnosis && diseaseData.diagnosis.content && diseaseData.diagnosis.content.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {diseaseData.diagnosis.heading || 'Diagnosis'}
              </h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <div>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="space-y-6">
                    {diseaseData.diagnosis.content.map((content, index) => (
                      <p key={index} className="text-gray-700 leading-relaxed text-lg">
                        {content}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Image based on disease type */}
              <div>
                {diseaseName === 'cataract-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Cataract_1.png" 
                      alt="Cataract diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'phaco-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Cataract_1.png" 
                      alt="Phaco surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'diabetic-retinopathy' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Diabetic_1.jpg" 
                      alt="Diabetic retinopathy diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'retinal-detachment' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Retinal_1.jpg" 
                      alt="Retinal detachment diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'contoura-vision' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Lasik_eye_surgery_1.jpg" 
                      alt="Contoura Vision diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'glaucoma-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Glaucoma_1.jpg" 
                      alt="Glaucoma diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'icl-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/ICL_SURGERY_1.jpg" 
                      alt="ICL surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'squint-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Squint_1.jpg" 
                      alt="Squint surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'vitrectomy-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Vitrectomy_1.jpg" 
                      alt="Vitrectomy surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'lasik-eye-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Lasik_eye_surgery_1.jpg" 
                      alt="LASIK eye surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'femto-lasik-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Lasik_eye_surgery_2.jpg" 
                      alt="Femto LASIK surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'smile-lasik-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Lasik_eye_surgery_3.jpg" 
                      alt="SMILE LASIK surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'prk-lasik-surgery' ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                    <img 
                      src="/image/Lasik_eye_surgery_4.jpg" 
                      alt="PRK LASIK surgery diagnosis and examination" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 text-center">
                    <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                      <EyeIcon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Expert Diagnosis</h3>
                    <p className="text-gray-600 text-lg">Professional evaluation and comprehensive assessment</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Treatment Section */}
      {diseaseData.treatment_section && diseaseData.treatment_section.treatments && diseaseData.treatment_section.treatments.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {diseaseData.treatment_section.heading || 'Treatment Options'}
              </h2>
              {diseaseData.treatment_section.intro && (
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {diseaseData.treatment_section.intro}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {diseaseData.treatment_section.treatments.map((treatment, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                      <HeartIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{treatment.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{treatment.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {diseaseData.treatment_section.outro && (
              <Card className="mt-12 max-w-4xl mx-auto">
                <CardContent className="p-8">
                  <div className="space-y-4">
                    {diseaseData.treatment_section.outro.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-gray-700 leading-relaxed">
                        {paragraph.trim()}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      {diseaseData.whyChooseUs && diseaseData.whyChooseUs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose TheLivCure for {formatDiseaseName(diseaseName)}?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Experience world-class treatment with our expert ophthalmologists
              </p>
              <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Reasons Grid */}
              <div className="grid grid-cols-1 gap-8">
                {diseaseData.whyChooseUs.map((reason, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                        {reason.number}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{reason.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Image based on disease type */}
              <div>
                {diseaseName === 'cataract-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Cataract_2.png" 
                      alt="Cataract surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'phaco-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Cataract_2.png" 
                      alt="Phaco surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'diabetic-retinopathy' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Diabetic_2.jpg" 
                      alt="Diabetic retinopathy treatment success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'retinal-detachment' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Retinal_2.jpg" 
                      alt="Retinal detachment treatment success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'contoura-vision' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Lasik_eye_surgery_2.jpg" 
                      alt="Contoura Vision treatment success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'glaucoma-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Glaucoma_2.jpg" 
                      alt="Glaucoma treatment success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'icl-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/ICL_SURGERY_2.jpg" 
                      alt="ICL surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'squint-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Squint_2.jpg" 
                      alt="Squint surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'vitrectomy-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Vitrectomy_2.jpg" 
                      alt="Vitrectomy surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'lasik-eye-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Lasik_eye_surgery_2.jpg" 
                      alt="LASIK eye surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'femto-lasik-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Lasik_eye_surgery_3.jpg" 
                      alt="Femto LASIK surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'smile-lasik-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Lasik_eye_surgery_4.jpg" 
                      alt="SMILE LASIK surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : diseaseName === 'prk-lasik-surgery' ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <img 
                      src="/image/Lasik_eye_surgery_5.jpg" 
                      alt="PRK LASIK surgery success and expertise" 
                      className="w-full h-auto rounded-xl mb-6"
                    />
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <HeartIcon className="w-10 h-10 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Expert Care</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      Trust our experienced team for world-class ophthalmology treatment and personalized care.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {formatDiseaseName(diseaseName)} - Image Gallery
            </h2>
            <p className="text-lg text-gray-600">
              Professional images showcasing our expertise in {formatDiseaseName(diseaseName).toLowerCase()} treatment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* LASIK Treatments Gallery */}
            {(diseaseName === 'lasik-eye-surgery' || diseaseName === 'femto-lasik-surgery' || 
              diseaseName === 'smile-lasik-surgery' || diseaseName === 'prk-lasik-surgery' || 
              diseaseName === 'contoura-vision') && (
              <>
                {[
                  { src: '/image/Lasik_eye_surgery_1.jpg', alt: 'LASIK Eye Surgery - Advanced Procedure' },
                  { src: '/image/Lasik_eye_surgery_3.jpg', alt: 'LASIK Eye Surgery - Expert Care' },
                  { src: '/image/Lasik_eye_surgery_4.jpg', alt: 'LASIK Eye Surgery - Modern Equipment' },
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Cataract Treatments Gallery */}
            {(diseaseName === 'cataract-surgery' || diseaseName === 'phaco-surgery') && (
              <>
                {[
                  { src: '/image/Cataract_1.png', alt: 'Cataract Surgery - Diagnosis and Examination' },
                  { src: '/image/Cataract_2.png', alt: 'Cataract Surgery - Advanced Treatment' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Diabetic Retinopathy Gallery */}
            {diseaseName === 'diabetic-retinopathy' && (
              <>
                {[
                  { src: '/image/Diabetic_1.jpg', alt: 'Diabetic Retinopathy - Advanced Diagnosis' },
                  { src: '/image/Diabetic_2.jpg', alt: 'Diabetic Retinopathy - Treatment Planning' },
                  { src: '/image/Diabetic_3.jpg', alt: 'Diabetic Retinopathy - Expert Care' },
                  { src: '/image/Diabetic_4.jpg', alt: 'Diabetic Retinopathy - Professional Team' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Retinal Detachment Gallery */}
            {diseaseName === 'retinal-detachment' && (
              <>
                {[
                  { src: '/image/Retinal_1.jpg', alt: 'Retinal Detachment - Expert Diagnosis' },
                  { src: '/image/Retinal_2.jpg', alt: 'Retinal Detachment - Advanced Treatment' },
                  { src: '/image/Retinal_3.jpg', alt: 'Retinal Detachment - Professional Care' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Glaucoma Surgery Gallery */}
            {diseaseName === 'glaucoma-surgery' && (
              <>
                {[
                  { src: '/image/Glaucoma_1.jpg', alt: 'Glaucoma Surgery - Expert Diagnosis' },
                  { src: '/image/Glaucoma_2.jpg', alt: 'Glaucoma Surgery - Advanced Treatment' },
                  { src: '/image/Glaucoma_3.jpg', alt: 'Glaucoma Surgery - Professional Care' },
                  { src: '/image/Glaucoma_4.jpg', alt: 'Glaucoma Surgery - Modern Technology' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ICL Surgery Gallery */}
            {diseaseName === 'icl-surgery' && (
              <>
                {[
                  { src: '/image/ICL_SURGERY_1.jpg', alt: 'ICL Surgery - Advanced Procedure' },
                  { src: '/image/ICL_SURGERY_2.jpg', alt: 'ICL Surgery - Precision Technology' },
                  { src: '/image/ICL_SURGERY_3.jpg', alt: 'ICL Surgery - Expert Care' },
                  { src: '/image/ICL_SURGERY_4.jpg', alt: 'ICL Surgery - Professional Team' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Squint Surgery Gallery */}
            {diseaseName === 'squint-surgery' && (
              <>
                {[
                  { src: '/image/Squint_1.jpg', alt: 'Squint Surgery - Expert Diagnosis' },
                  { src: '/image/Squint_2.jpg', alt: 'Squint Surgery - Advanced Treatment' },
                  { src: '/image/Squint_3.jpg', alt: 'Squint Surgery - Professional Care' },
                  { src: '/image/Squint_4.jpg', alt: 'Squint Surgery - Modern Technology' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Vitrectomy Surgery Gallery */}
            {diseaseName === 'vitrectomy-surgery' && (
              <>
                {[
                  { src: '/image/Vitrectomy_1.jpg', alt: 'Vitrectomy Surgery - Advanced Procedure' },
                  { src: '/image/Vitrectomy_2.jpg', alt: 'Vitrectomy Surgery - Precision Technology' },
                  { src: '/image/Vitrectomy_3.jpg', alt: 'Vitrectomy Surgery - Expert Care' },
                  { src: '/image/Vitrectomy_4.jpg', alt: 'Vitrectomy Surgery - Professional Team' }
                ].map((image, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white bg-opacity-90 rounded-full p-2">
                          <EyeIcon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {diseaseData.faqs && diseaseData.faqs.length > 0 && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600">
                Common questions about {formatDiseaseName(diseaseName)} treatment
              </p>
            </div>

            <div className="space-y-4">
              {diseaseData.faqs.map((faq, index) => (
                <Card key={index} className="overflow-hidden">
                  <CardContent className="p-0">
                    <button
                      className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </h3>
                        {expandedFAQ === index ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                    {expandedFAQ === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready for Your {formatDiseaseName(diseaseName)} Treatment?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Book a free consultation with our expert ophthalmologists and take the first step towards better vision
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-600" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from the TheLivCure site.")}>
              <PhoneIcon className="w-5 h-5 mr-2" />
              Call Now: +91 93117 20045
            </Button>
            <BookAppointmentModal treatmentType={diseaseData?.title || formatDiseaseName(diseaseName)}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                <CalendarIcon className="w-5 h-5 mr-2" />
                Book Online Consultation
              </Button>
            </BookAppointmentModal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OphthalmologyPage;